import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';

const OPTIMISTIC_PREFIX_BUCKET = '__optimistic__';

const normalizePrefix = (prefix) => (typeof prefix === 'string' ? prefix.trim().toUpperCase() : '');

const removePrefixFromCache = (allExistingPrefixes, prefixToRemove) => {
  const normalizedPrefix = normalizePrefix(prefixToRemove);

  if (!normalizedPrefix || !allExistingPrefixes || typeof allExistingPrefixes !== 'object') {
    return allExistingPrefixes || {};
  }

  return Object.entries(allExistingPrefixes).reduce((nextPrefixes, [key, prefixes]) => {
    const safePrefixes = Array.isArray(prefixes) ? prefixes : [];
    const filteredPrefixes = safePrefixes.filter(prefix => normalizePrefix(prefix) !== normalizedPrefix);

    if (filteredPrefixes.length > 0) {
      nextPrefixes[key] = filteredPrefixes;
    }

    return nextPrefixes;
  }, {});
};

const addPrefixToCache = (allExistingPrefixes, prefixToAdd) => {
  const normalizedPrefix = normalizePrefix(prefixToAdd);

  if (!normalizedPrefix) {
    return allExistingPrefixes || {};
  }

  const nextPrefixes = {
    ...(allExistingPrefixes && typeof allExistingPrefixes === 'object' ? allExistingPrefixes : {})
  };
  const existingPrefixes = Object.values(nextPrefixes).flat().map(normalizePrefix);

  if (existingPrefixes.includes(normalizedPrefix)) {
    return nextPrefixes;
  }

  const optimisticPrefixes = Array.isArray(nextPrefixes[OPTIMISTIC_PREFIX_BUCKET])
    ? nextPrefixes[OPTIMISTIC_PREFIX_BUCKET]
    : [];

  nextPrefixes[OPTIMISTIC_PREFIX_BUCKET] = [...optimisticPrefixes, normalizedPrefix];

  return nextPrefixes;
};

const syncPrefixCache = (allExistingPrefixes, { previousPrefix, nextPrefix }) => {
  if (normalizePrefix(previousPrefix) === normalizePrefix(nextPrefix)) {
    return allExistingPrefixes || {};
  }

  const withoutPreviousPrefix = removePrefixFromCache(allExistingPrefixes, previousPrefix);
  return addPrefixToCache(withoutPreviousPrefix, nextPrefix);
};

const getApiErrorMessage = (error, fallback = 'Request failed') => {
  const responseData = error?.response?.data;

  return (
    responseData?.error ||
    responseData?.message ||
    responseData?.data?.error ||
    responseData?.data?.message ||
    error?.message ||
    fallback
  );
};

// Async thunks
export const fetchProjects = createAsyncThunk('projects/fetchProjects', async () => {
  const response = await api.get(ENDPOINTS.PROJECTS);
  if (!response?.data?.data) {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const fetchDropdownData = createAsyncThunk('projects/fetchDropdownData', async () => {
  const response = await api.get(ENDPOINTS.PROJECTS_DROPDOWNS);
  if (!response?.data?.data || typeof response.data.data !== 'object') {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const fetchProjectMembers = createAsyncThunk('projects/fetchProjectMembers', async (params = {}) => {
  const isObjectArg = params && typeof params === 'object' && !Array.isArray(params);
  const projectId = isObjectArg ? params.projectId ?? params.project_id ?? null : params;
  const statusValues = isObjectArg ? (Array.isArray(params.status) ? params.status : []) : [];

  const queryParams = new URLSearchParams();
  if (projectId) queryParams.append('project_id', projectId);
  statusValues.forEach((status) => {
    if (status) queryParams.append('status', status);
  });

  const url = queryParams.toString()
    ? `/projects/dropdowns?${queryParams.toString()}`
    : ENDPOINTS.PROJECTS_DROPDOWNS;

  const response = await api.get(url);
  if (!response?.data?.data || typeof response.data.data !== 'object') {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const createProject = createAsyncThunk('projects/createProject', async (projectData) => {
  const response = await api.post(ENDPOINTS.PROJECTS, projectData);
  return response.data;
});

export const updateProject = createAsyncThunk('projects/updateProject', async ({ id, data }) => {
  const response = await api.put(ENDPOINTS.PROJECT_BY_ID(id), data);
  return response.data;
});

export const deleteProject = createAsyncThunk('projects/deleteProject', async (id) => {
  const projectId = typeof id === 'object' ? id?.id : id;
  await api.delete(ENDPOINTS.PROJECT_BY_ID(projectId));
  return id;
});

export const fetchAllExistingPrefixes = createAsyncThunk('projects/fetchAllExistingPrefixes', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.PROJECTS_EXISTING_PREFIXES);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const projectSlice = createSlice({
  name: 'projects',
  initialState: {
    projects: {},
    spaces: [],
    users: [],
    statusOptions: [],
    projectMembers: [],
    allExistingPrefixes: {},
    loading: false,
    dropdownsLoading: false,
    error: null
  },
  reducers: {
    clearProjectMembers: (state) => {
      state.projectMembers = [];
    },
    clearExistingPrefixes: (state) => {
      state.allExistingPrefixes = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          state.projects = action.payload;
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchDropdownData.pending, (state) => {
        state.dropdownsLoading = true;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.dropdownsLoading = false;
        if (action.payload && typeof action.payload === 'object') {
          if (Array.isArray(action.payload.spaces)) {
            state.spaces = action.payload.spaces;
          }
          if (Array.isArray(action.payload.project_status)) {
            state.statusOptions = action.payload.project_status;
          }
        }
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.dropdownsLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProjectMembers.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object') {
          state.projectMembers = Array.isArray(action.payload.project_users) ? action.payload.project_users : [];
          state.statusOptions = Array.isArray(action.payload.project_status) 
            ? action.payload.project_status 
            : (Array.isArray(action.payload.status) ? action.payload.status : []);
        }
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects = {};
        state.allExistingPrefixes = addPrefixToCache(
          state.allExistingPrefixes,
          action.meta.arg?.ticket_number_prefix
        );
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.projects = {};
        state.allExistingPrefixes = syncPrefixCache(state.allExistingPrefixes, {
          previousPrefix: action.meta.arg?.previousPrefix,
          nextPrefix: action.meta.arg?.data?.ticket_number_prefix
        });
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = {};
        state.allExistingPrefixes = removePrefixFromCache(
          state.allExistingPrefixes,
          action.payload?.prefix || action.meta.arg?.prefix
        );
      })
      .addCase(fetchAllExistingPrefixes.fulfilled, (state, action) => {
        state.allExistingPrefixes = action.payload || {};
      })
      .addCase(fetchAllExistingPrefixes.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  }
});

export const { clearProjectMembers, clearExistingPrefixes } = projectSlice.actions;
export default projectSlice.reducer;
